import type { layout } from "@app/domain";
import type { SceneId } from "@app/protocol";
import { sceneOf } from "@app/ui";
import type { ReactNode } from "react";
import { Room, TableArt } from "./Scene";

/**
 * The canvas root (ADR 0011): the scene's room fills the viewport; everything
 * else lives on the fixed design canvas under one transform. `table` puts the
 * scene's table picture at the canvas surface (ADR 0023); `overlay` covers the
 * whole viewport, letterbox included.
 */
export function Stage({
  measure,
  scene,
  children,
  table = true,
  overlay,
}: {
  measure: layout.Measure;
  scene: SceneId;
  children: ReactNode;
  table?: boolean;
  overlay?: ReactNode;
}) {
  const { canvas, scale, offsetX, offsetY } = measure;
  const s = sceneOf(scene);
  return (
    <div
      className="relative h-full w-full overflow-hidden bg-night font-ui text-ink"
      data-layout={measure.state}
      data-scene={s.id}
    >
      <Room scene={s} />
      <div
        className="absolute left-0 top-0 isolate origin-top-left"
        style={{
          width: canvas.w,
          height: canvas.h,
          transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
        }}
      >
        {table ? <TableArt scene={s} rect={canvas.surface} turned={canvas.turned} /> : null}
        {children}
      </div>
      {overlay}
    </div>
  );
}

/** Places a child at a canvas element's rect, in canvas units. */
export function Slot({
  rect,
  children,
  label,
  className = "",
}: {
  rect: layout.Rect;
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <section
      className={`absolute ${className}`}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      aria-label={label}
    >
      {children}
    </section>
  );
}
